import { API_BASE_URL } from "../../constant/constant";
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  SkillsContainer,
  SkillCard,
  SkillHeader,
  SkillIcon,
  SkillName,
  RunButton,
  Tag,
  SearchContainer,
  SkillDescription,
} from "./style";
import play from "../../assets/play.svg";
import {
  StyledTextField,
  StyledSelect,
  filterTags,
  tagColors,
} from "../Constants/Constants";
import { handleFileUploadWithSignedURL } from "../Environment/Asconfig";
import { toast } from "react-toastify";
import MenuItem from "@mui/material/MenuItem";
import SearchIcon from "../../assets/search.png";
import InputAdornment from "@mui/material/InputAdornment";
import Popovers from "./Popovers";
import SkillConfigPopover from "./SkillConfigPopover";
import { useSnackbar } from "../../context/SnackBarContext";



const SkillsSection = ({ onRunSkill }) => {
  const { t } = useTranslation();

  const [sortBy, setSortBy] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [workflows, setWorkflows] = useState([]);
  const [userSkills, setUserSkills] = useState([]);
  const [nonSkillTools, setNonSkillTools] = useState([]);
  const [skillTools, setSkillTools] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedTool, setSelectedTool] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [showError, setShowError] = useState(false);
  const [isDriveFilesLoading, setIsDriveFilesLoading] = useState(false);
  const [skillConfigAnchorEl, setSkillConfigAnchorEl] = useState(null);
  const [selectedSkillForConfig, setSelectedSkillForConfig] = useState(null);
  const [skillConfigValues, setSkillConfigValues] = useState({});
  const showSnackbar = useSnackbar();

  const fetchWorkflows = async () => {
    try {
      const userId = localStorage.getItem("id");
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_BASE_URL}/api/workflow/getworkflows?user_id=${userId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-auth-token": token || "",
          },
        }
      );

      const data = await response.json();
      if (data.responseCode === 200) {
        setWorkflows(data.workflows);
      }
      else if (data.responseCode === 429) {
        showSnackbar(data.message, "error");
      }
    } catch (error) {
      console.error("Error fetching workflows:", error);
    }
  };

  const fetchUserSkills = async () => {
    try {
      const userId = localStorage.getItem("id");
      const token = localStorage.getItem("token");

      const response = await fetch(
        `${API_BASE_URL}/api/skill/user/${userId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-auth-token": token || "",
          },
        }
      );

      const data = await response.json();

      if (data.success) {
        setUserSkills(data.skills);
      }
      else if (data.responseCode === 429) {
        showSnackbar(data.message, "error");
      }
    } catch (error) {
      console.error("Error fetching user skills:", error);
    }
  };


  const fetchNonSkillTools = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_BASE_URL}/api/tool/non-skill-tools`,
        {
          headers: {
            "x-auth-token": token || "",
          },
        }
      );
      const data = await response.json();
      if (data.responseCode === 200) {
        setNonSkillTools(data.tools);
      }
      else if (data.responseCode === 429) {
        showSnackbar(data.message, "error");
      }
    } catch (error) {
      console.error("Error fetching non-skill tools:", error);
    }
  };

  const fetchSkillTools = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_BASE_URL}/api/tool/skill-tools`,
        {
          headers: {
            "x-auth-token": token || "",
          },
        }
      );
      const data = await response.json();
      if (data.responseCode === 200) {
        setSkillTools(data.tools);
      }
      else if (data.responseCode === 429) {
        showSnackbar(data.message, "error");
      }
    } catch (error) {
      console.error("Error fetching skill tools:", error);
    }
  };

  useEffect(() => {
    fetchWorkflows();
    fetchUserSkills();
    fetchNonSkillTools();
    fetchSkillTools();
  }, []);

  const uploadFileToS3 = async (file) => {
    try {
      const response = await handleFileUploadWithSignedURL(file);
      return response.fileUrl;
    } catch (error) {
      toast.error(error);
      return null;
    }
  };

  const handleDriveFiles = async (fileLinks) => {
    setIsDriveFilesLoading(true);

    if (!fileLinks || fileLinks.length === 0) {
      setIsDriveFilesLoading(false);
      return;
    }

    const fileId = fileLinks[0];
    const apiKey = process.env.REACT_APP_API_KEY;

    try {
      let fileUrl = `https://www.googleapis.com/drive/v3/files/${fileId}?alt=media&key=${apiKey}`;
      let response = await fetch(fileUrl);

      if (!response.ok) {
        response = await fetch(fileUrl, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        });
      }

      const blobData = await response.blob();
      const metadataUrl = `https://www.googleapis.com/drive/v3/files/${fileId}?key=${apiKey}&fields=name,mimeType`;
      let metadataResponse = await fetch(metadataUrl);

      if (!metadataResponse.ok) {
        metadataResponse = await fetch(metadataUrl, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        });
      }

      const metadata = await metadataResponse.json();
      const file = new File([blobData], metadata.name, {
        type: metadata.mimeType,
      });

      const s3Url = await uploadFileToS3(file);
      if (!s3Url) throw new Error("S3 upload failed");

      const newFile = {
        name: metadata.name,
        type: metadata.mimeType,
        isDrive: true,
        driveUrl: s3Url,
        id: fileId,
      };

      setUploadedFiles((prevFiles) => [...prevFiles, newFile]);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsDriveFilesLoading(false);
    }
  };

  const handleRunTool = (tool, event) => {
    let config = [];
    try {
      config =
        typeof tool.configuration === "string"
          ? JSON.parse(tool.configuration)
          : tool.configuration || [];
    } catch (e) {
      config = [];
    }

    if (config.length > 0) {
      setSelectedSkillForConfig(tool);
      setSkillConfigAnchorEl(event.currentTarget);
      const initialValues = {};
      config.forEach((c) => {
        initialValues[c.title] = "";
      });
      setSkillConfigValues(initialValues);
    } else if (tool.isRequiredDocument) {
      setSelectedTool(tool);
      setAnchorEl(event.currentTarget);
    } else {
      onRunSkill?.(tool.toolTrigger, [], tool);
    }
  };

  const handleRunWorkflow = (workflow) => {
    onRunSkill?.(`Run the workflow ${workflow.name}`, [], { isSkill: false, isWorkflow: true, ...workflow });
  };

  const handleRunUserSkill = (skillName) => {
    onRunSkill?.(`Run the skill ${skillName}`, [], { isSkill: true });
  };
  const handleRunSkillTool = (skill, event) => {
    console.log(skill);

    let config = [];
    try {
      config =
        typeof skill.configuration === "string"
          ? JSON.parse(skill.configuration)
          : skill.configuration || [];
    } catch (e) {
      config = [];
    }

    if (config.length > 0) {
      setSelectedSkillForConfig(skill);
      setSkillConfigAnchorEl(event.currentTarget);
      const initialValues = {};
      config.forEach((c) => {
        initialValues[c.title] = "";
      });
      setSkillConfigValues(initialValues);
    } else if (skill.isRequiredDocument) {
      setSelectedTool({ ...skill, isSkill: true });
      setAnchorEl(event.currentTarget);
    } else {
      onRunSkill?.(`Run the skill ${skill.toolName}`, [], {
        isSkill: true,
        ...skill,
      });
    }
  };

  const handleCloseSkillConfig = () => {
    setSkillConfigAnchorEl(null);
    setSelectedSkillForConfig(null);
    setSkillConfigValues({});
    setUploadedFiles([]);
  };

  const handleSaveSkillConfig = (message, skill, skillFiles = []) => {
    console.log(message, skill, skillFiles);
    onRunSkill?.(message, skillFiles, { isSkill: skill.isSkill !== false, ...skill });
    handleCloseSkillConfig();
  };


  const handleClosePopover = () => {
    setAnchorEl(null);
    setSelectedTool(null);
    setUploadedFiles([]);
    setShowError(false);
  };

  const handleFileUpload = (files) => {
    const newFiles = Array.from(files).map((file) => ({
      name: file.name,
      type: file.type,
      file,
      isDrive: false,
    }));
    setUploadedFiles((prev) => [...prev, ...newFiles]);
    setShowError(false);
  };

  const handleRemoveFile = (fileName) => {
    setUploadedFiles((prev) => prev.filter((file) => file.name !== fileName));
  };

  const handleSave = () => {
    if (uploadedFiles.length === 0) {
      setShowError(true);
      return;
    }
    if (selectedTool) {
      // Check if it's a skill tool (from /skill-tool endpoint)
      if (selectedTool.isSkill) {
        onRunSkill?.(`Run the skill ${selectedTool.toolName}`, uploadedFiles, selectedTool);
      } else {
        onRunSkill?.(selectedTool.toolTrigger, uploadedFiles, selectedTool);
      }
    }
    handleClosePopover();
  };

  const filterBySearchAndTag = (items, nameKey, tag) =>
    items.filter((item) => {
      const nameMatch = item[nameKey]
        .toLowerCase()
        .includes(searchQuery.toLowerCase());
      const tagMatch = !sortBy || tag.toLowerCase() === sortBy.toLowerCase();
      return nameMatch && tagMatch;
    });

  const hiddenNonSkillToolNames = [
    "Analyseverslag",
    "Blogpostgeneratie",
    "Chatten met document",
    "Document samenvatten",
    "Documenten ophalen in database",
    "Documenten opvragen in database",
    "Doeleinde van bestaansdocument aanmaken",
    "Genereer webpagina-inhoud",
  ];

  const filteredNonSkillTools = nonSkillTools.filter((tool) => {
    if (hiddenNonSkillToolNames.includes(tool.toolName)) return false;

    const matchesSearch = tool.toolName
      .toLowerCase()
      .includes(searchQuery.toLowerCase());

    const toolTags = tool.toolTag
      ? tool.toolTag
        .split(",")
        .map((tag) => tag.trim().toLowerCase())
      : [];

    const matchesTag = !sortBy || toolTags.includes(sortBy.toLowerCase());

    return matchesSearch && matchesTag;
  });

  const filteredSkillTools = skillTools.filter((tool) => {
    const matchesSearch = tool.toolName
      .toLowerCase()
      .includes(searchQuery.toLowerCase());

    const toolTags = tool.toolTag
      ? tool.toolTag
        .split(",")
        .map((tag) => tag.trim().toLowerCase())
      : [];

    const matchesTag = !sortBy || toolTags.includes(sortBy.toLowerCase());

    return matchesSearch && matchesTag;
  });

  const filteredWorkflows = workflows.filter((workflow) => {
    const matchesSearch = workflow.name
      .toLowerCase()
      .includes(searchQuery.toLowerCase());

    const matchesTag = !sortBy || sortBy === "workflow";

    return matchesSearch && matchesTag;
  });

  const filteredUserSkills = userSkills.filter((skill) => {
    const matchesSearch = skill.name
      .toLowerCase()
      .includes(searchQuery.toLowerCase());

    const matchesTag = !sortBy || sortBy === "skill";

    return matchesSearch && matchesTag;
  });

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "1.25rem",
        width: "100%",
        padding: "1rem",
      }}
    >
      <SearchContainer>
        <StyledTextField
          placeholder={t("avatarChat.searchSkills")}
          variant="outlined"
          fullWidth
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"></InputAdornment>,
          }}
        />

        <StyledSelect
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          displayEmpty
          size="small"
          style={{ minWidth: 120 }}
        >
          <MenuItem value="">
            {t("avatarChat.allCategories")}
          </MenuItem>
          {filterTags.map((tag) => (
            <MenuItem key={tag.value} value={tag.value}>
              {tag.label}
            </MenuItem>
          ))}
        </StyledSelect>
      </SearchContainer>

      <SkillsContainer>
        {filteredNonSkillTools.map((tool) => (
          <SkillCard key={tool._id}>
            <SkillHeader>
              <SkillIcon>
                <img src={play} alt="play" />
              </SkillIcon>
              <SkillName>{tool.toolName}</SkillName>
            </SkillHeader>

            <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
              {tool.toolTag?.split(",").map((tag, index) => {
                const trimmedTag = tag.trim();
                if (!trimmedTag) return null;
                return (
                  <Tag
                    key={index}
                    style={{
                      background:
                        tagColors[trimmedTag.toLowerCase()]?.bg ??
                        "rgba(26, 116, 219, 0.2)",
                      color:
                        tagColors[trimmedTag.toLowerCase()]?.text ?? "#1a74db",
                    }}
                  >
                    {trimmedTag}
                  </Tag>
                );
              })}
            </div>

            <SkillDescription>
              {tool.toolDescription}
            </SkillDescription>

            <RunButton onClick={(e) => handleRunTool(tool, e)}>
              {t("avatarChat.run")}
            </RunButton>
          </SkillCard>
        ))}

        {filteredWorkflows.map((workflow) => (
          <SkillCard key={workflow._id}>
            <SkillHeader>
              <SkillIcon>
                <img src={play} alt="play" />
              </SkillIcon>
              <SkillName>{workflow.name}</SkillName>
            </SkillHeader>

            <Tag>{t("avatarChat.workflow")}</Tag>

            <SkillDescription>
              {workflow.description}
            </SkillDescription>

            <RunButton onClick={() => handleRunWorkflow(workflow)}>
              {t("avatarChat.run")}
            </RunButton>
          </SkillCard>
        ))}

        {filteredUserSkills.map((skill) => (
          <SkillCard key={skill._id}>
            <SkillHeader>
              <SkillIcon>
                <img src={play} alt="play" />
              </SkillIcon>
              <SkillName>{skill.name}</SkillName>
            </SkillHeader>

            {/* <Tag>{t("avatarChat.skill")}</Tag> */}
            <Tag>Skill</Tag>

            <SkillDescription>
              {skill.objectives}
            </SkillDescription>

            <RunButton onClick={() => handleRunUserSkill(skill.name)}>
              {t("avatarChat.run")}
            </RunButton>
          </SkillCard>
        ))}

        {filteredSkillTools.map((skill) => (
          <SkillCard key={skill._id}>
            <SkillHeader>
              <SkillIcon>
                <img src={play} alt="play" />
              </SkillIcon>
              <SkillName>{skill.toolName}</SkillName>
            </SkillHeader>

            {/* <Tag>{t("avatarChat.skill")}</Tag> */}
            <Tag>Skill</Tag>

            <SkillDescription>
              {skill.toolDescription}
            </SkillDescription>

            <RunButton onClick={(e) => handleRunSkillTool(skill, e)}>
              {t("avatarChat.run")}
            </RunButton>
          </SkillCard>
        ))}
      </SkillsContainer>

      <Popovers
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        onFileUpload={handleFileUpload}
        onRemoveFile={handleRemoveFile}
        onSave={handleSave}
        uploadedFiles={uploadedFiles}
        showError={showError}
        selectedTool={selectedTool}
        handleDriveFiles={handleDriveFiles}
      />

      <SkillConfigPopover
        anchorEl={skillConfigAnchorEl}
        onClose={handleCloseSkillConfig}
        onSave={handleSaveSkillConfig}
        skill={selectedSkillForConfig}
        configValues={skillConfigValues}
        setConfigValues={setSkillConfigValues}
        onFileUpload={handleFileUpload}
        onRemoveFile={handleRemoveFile}
        uploadedFiles={uploadedFiles}
        handleDriveFiles={handleDriveFiles}
        showError={showError}
        isDriveFilesLoading={isDriveFilesLoading}
      />
    </div>
  );
};

export default SkillsSection;
